import React from 'react';
import 'remirror/styles/all.css';
import {
    BoldExtension,
    BulletListExtension,
    ItalicExtension,
    OrderedListExtension,
    LinkExtension,
    HardBreakExtension,
    HeadingExtension,
    SubExtension,
    SupExtension
} from 'remirror/extensions';
import {
    Remirror,
    EditorComponent,
    useRemirror,
    useActive,
    useCommands
} from '@remirror/react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { Link, ListOl, ListUl, TypeBold, TypeItalic } from 'react-bootstrap-icons';

const extensions = () => [
    new BoldExtension({}),
    new ItalicExtension(),
    new BulletListExtension({}),
    new OrderedListExtension(),
    new LinkExtension({}),
    new HardBreakExtension({}),
    new HeadingExtension({}),
    new SubExtension({}),
    new SupExtension({}),
];

type Props = {
    value: string,
    onChange: (html: string) => void
}

const RichTextField = ({ value, onChange }: Props) => {
    return (
        <div className="RichTextField">
            <Editor onChange={onChange} content={value} />
        </div>
    )
};

export default RichTextField;

type EditorProps = { onChange: (html: string) => void, content: string };
const Editor = ({ onChange, content }: EditorProps) => {
    const { manager, state, setState } = useRemirror({
        extensions,
        content,
        stringHandler: 'html',
        selection: 'end',
    });
    const lastContentRef = React.useRef(content);
    const handleEditorChange = React.useCallback((parameter: any) => {
        setState(parameter.state);
        const html = parameter.helpers.getHTML(parameter.state);
        if (html === lastContentRef.current) {
            return;
        }
        lastContentRef.current = html;
        onChange(html);
    }, [onChange, setState]);

    React.useEffect(() => {
        if (content === lastContentRef.current) {
            return;
        }
        lastContentRef.current = content;
        const nextState = manager.createState({ content, stringHandler: 'html' });
        setState(nextState);
    }, [content, manager, setState]);

    return (
        <div className='Editor'>
            <Remirror manager={manager} state={state} onChange={handleEditorChange}>
                <Menu />
                <EditorComponent />
            </Remirror>
        </div>
    );
};

const Menu = () => {
    const commands = useCommands();
    const active = useActive();

    const boldClick = () => {
        commands.toggleBold();
    };

    const italicClick = () => {
        commands.toggleItalic();
    };

    const bulletListClick = () => {
        commands.toggleBulletList();
    };

    const orderedListClick = () => {
        commands.toggleOrderedList();
    };

    const linkClick = () => {
        const activeLink = active.link();

        if (activeLink) {
            commands.removeLink();
        } else {
            const url = prompt('Enter the URL', 'https://');
            if (url) {
                commands.updateLink({ href: url, target: '_blank' });
            }
        }
    };

    const headingClick = () => {
        commands.toggleHeading({ level: 2 });
    };

    const subClick = () => {
        commands.toggleSubscript();
    };

    const supClick = () => {
        commands.toggleSuperscript();
    };

    return (
        <div style={{ padding: '0.25rem 0' }}>
            <ButtonGroup>
                <Button variant='editor' active={active.heading({ level: 2 })} onClick={headingClick}>
                    H2
                </Button>
                <Button variant='editor' active={active.bold()} onClick={boldClick}><TypeBold /></Button>
                <Button variant='editor' active={active.italic()} onClick={italicClick}><TypeItalic /></Button>
            </ButtonGroup>
            <div className="vr"></div>
            <ButtonGroup>
                <Button variant='editor' active={active.bulletList()} onClick={bulletListClick}><ListUl /></Button>
                <Button variant='editor' active={active.orderedList()} onClick={orderedListClick}><ListOl /></Button>
            </ButtonGroup>
            <div className="vr"></div>
            <ButtonGroup>
                <Button variant='editor' active={active.link()} onClick={linkClick}><Link /></Button>
            </ButtonGroup>
            <div className="vr"></div>
            <ButtonGroup>
                <Button variant='editor' active={active.sub()} onClick={subClick}>
                    x₂
                </Button>
                <Button variant='editor' active={active.sup()} onClick={supClick}>
                    x²
                </Button>
            </ButtonGroup>
        </div>
    );
};
